
import { mapGetters, mapActions } from 'vuex'
import * as actionTypes from '@/store/action-types'
import variantTypes from '@/store/variant-types'

const TIME_PERIOD_OPTIONS = [
  { label: 'All Time', value: 'allTime' },
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'Last 3 Months', value: 'last3Months' }
]

function findChampionVariant(variants) {
  return variants.find(v => v.form_variant_type?.type === variantTypes.CHAMPION) || variants[0]
}

export default {
  data() {
    return {
      snackbar: {
        show: false,
        message: null,
        color: null
      },
      filter: {
        variant: null,
        timePeriod: null,
        isFilterSet: false
      },
    }
  },

  mounted() {
    if (!this.filter.timePeriod) {
      this.filter.timePeriod = this.timePeriodOptions[0]?.value
    }
    this.fetchAllVariants()
  },

  computed: {
    ...mapGetters(['getFormVariants']),
    ...mapGetters('analytics', ['getVariantAnalyticsData']),

    variants() {
      return this.getFormVariants(parseInt(this.$route.params.id)) || []
    },

    timePeriodOptions() {
      return TIME_PERIOD_OPTIONS
    }
  },

  methods: {
    ...mapActions('analytics', ['fetchVariantAnalytics']),

    isAnalyticsEnabled() {
      return this.$options.mixinOptions?.enableAnalytics === true
    },

    async fetchAllVariants() {
      const formId = this.$route.params.id

      try {
        const response = await this.$store.dispatch(actionTypes.FETCH_FORM_VARIANTS, { formId })
        const variants = response?.data?.data || []
        await this.$store.dispatch(actionTypes.FETCH_FORM_VARIANTS_SUCCESS, variants)
        this.selectChampionVariant(variants)
      } catch (err) {
        this.showSnackbar(err?.response?.data?.meta?.error_message || 'Error fetching form variants.', true)
      }
    },

    async fetchTrashedVariants(trashed = 1) {
      const formId = this.$route.params.id

      try {
        const response = await this.$store.dispatch(actionTypes.FETCH_FORM_VARIANTS, {
          formId,
          params: { trashed }
        })
        const variants = response?.data?.data || []
        await this.$store.dispatch(actionTypes.FETCH_FORM_VARIANTS_SUCCESS, variants)
        this.fetchLeads?.()
      } catch (err) {
        this.showSnackbar(err?.response?.data?.meta?.error_message || 'Error fetching trashed variants.', true)
      }
    },

    selectChampionVariant(variants, selected = null) {
      const champion = findChampionVariant(variants)
      if (!champion) return null

      const target = selected || this.selected || this.filter

      if (target && !target.variant) {
        target.variant = champion.id
      }

      if (this.isAnalyticsEnabled?.()) {
        this.fetchAnalyticsForCurrentSelection()
      }

      return champion
    },

    async fetchAnalyticsForCurrentSelection() {
      if (!this.isAnalyticsEnabled()) return

      const formId = parseInt(this.$route.params.id)
      const variantId = this.filter.variant
      const timePeriod = this.filter.timePeriod

      if (!formId || !variantId || !timePeriod) {
        this.showSnackbar('Please select a variant and time period.', true)
        return
      }

      try {
        await this.fetchVariantAnalytics({ formId, variantId, timePeriod })
      } catch (error) {
        this.showSnackbar(error.message || 'Failed to load analytics.', true)
      }
    },

    applyFilter() {
      this.filter.isFilterSet = true
      this.fetchAnalyticsForCurrentSelection()
    },

    async syncLeadData(syncAll = false) {
      if (!this.isAnalyticsEnabled()) {
        this.showSnackbar('Analytics is not enabled for this component.', true)
        this.loader.syncLead = false
        return
      }

      const formId = parseInt(this.$route.params.id)
      const variantId = this.filter.variant
      const timePeriod = this.filter.timePeriod

      if (!formId || !variantId || !timePeriod) {
        this.showSnackbar('Please select a variant and time period before syncing.', true)
        this.loader.syncLead = false
        return
      }

      try {
        // Store current selection
        const currentVariant = this.filter.variant
        const currentTimePeriod = this.filter.timePeriod

        if (syncAll) {
          // For sync all, we need to fetch data for the current selection first
          await this.fetchVariantAnalytics({ formId, variantId, timePeriod, forceRefresh: true })
          this.showSnackbar('All data synced successfully!', false)
        } else {
          // For current selection sync
          await this.fetchVariantAnalytics({ formId, variantId, timePeriod, forceRefresh: true })
          this.showSnackbar('Current selection synced successfully!', false)
        }

        // Hide loading state
        this.loader.syncLead = false
        this.loader.showSync = false

      } catch (error) {
        this.showSnackbar(error.message || 'Failed to sync data. Please try again.', true)
        this.loader.syncLead = false
      }
    },

    markLeadAsViewed(ids, isFormList = false, showSuccessAlert = false) {
      const key = isFormList ? 'formId' : 'formVariantId'
      return this.$store.dispatch('formLeads/' + actionTypes.MARK_READ_LEAD, {
        // key formId if formlist else variantId
        [key]: ids
      }).then((response) => {
        if (showSuccessAlert) {
          const message = response?.data?.meta?.message || response?.data?.message
          if (message) {
            this.showSnackbar(message, false)
          }
        }
      }, (err) => {
        this.$store.dispatch('formLeads/' + actionTypes.MARK_READ_LEAD_ERROR, null)
        return Promise.reject(err)
      })
    },

    showSnackbar(message, isError = false) {
      this.snackbar = {
        show: true,
        message,
        color: isError ? 'danger-color' : 'success-color'
      }
    },

    truncateText(text, originalMaxLength = 30, displayLength = 27) {
      if (!text) {
        return 'None'
      }
      if (text?.length > originalMaxLength) {
        return text?.substring(0, displayLength) + '...'
      }
      return text
    }
  }
}
